import {Box, Snackbar, type SnackbarCloseReason} from '@mui/material';
import {selectAlert, setAlert} from '@smartmixin/core';
import {messages} from '@smartmixin/i18n';
import React from 'react';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import {Banner, type BannerSeverity} from '../../components/ui';
import {elevation, radius} from '../../theme/tokens';

const SEVERITY: Record<string, BannerSeverity> = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
};

/**
 * How long an alert stays, whatever its severity (WCAG 2.2.1, Timing
 * Adjustable): long enough to read, and the clock stops while the pointer or
 * the focus is on it. No alert waits to be dismissed, a warning or an error
 * included: one that must stay readable belongs on the screen itself (a
 * Banner, a field's error), not here.
 */
export const HIDE_MS = 6000;

/**
 * The app's alert ("Station added to favorites", a request's error): a
 * ui/Banner floating at the bottom right — information is announced
 * politely, a problem interrupts — with a translated close button.
 *
 * Every alert goes after 6 s — the clock stopped while the pointer or the
 * focus is on it, and restarted in full after — or sooner with the close
 * button, or Escape with the focus inside it (Escape elsewhere belongs to
 * whatever has the focus: a dialog, a keyboard drag being cancelled). A click
 * elsewhere is not a dismissal.
 *
 * Mounted inside the shell (AppContainer), which sets `--snackbar-bottom`:
 * above the phone's bottom bar, and above the floating forecast-model chip
 * when a station shows one. MUI's own offsets (8px phone, 24px from 600px)
 * are what it falls back to anywhere else.
 */
const AlertSnackbar = () => {
  const {formatMessage} = useIntl();
  const dispatch = useDispatch();
  const alert = useSelector(selectAlert);
  // Which alert holds the focus. Dismissing removes the focused node with no
  // blur (Chrome focuses the close button on mousedown), so the focus is
  // dropped with the alert: otherwise the next alert with the same message
  // would never go.
  const [focusedKey, setFocusedKey] = React.useState<string | null>(null);
  if (!alert && focusedKey !== null) {
    setFocusedKey(null);
  }

  const handleClose = React.useCallback(() => {
    setFocusedKey(null);
    dispatch(setAlert(null));
  }, [dispatch]);
  // The timer only. MUI also closes on a click anywhere and on Escape
  // anywhere in the document; both are handled (or ignored) here instead.
  const handleSnackbarClose = React.useCallback(
    (_event: unknown, reason?: SnackbarCloseReason) => {
      if (reason === 'timeout') {
        handleClose();
      }
    },
    [handleClose],
  );

  // Every setAlert stores a new object, even with the same text: a retry that
  // fails the same way ("Invalid API key" twice) is a new alert, and gets its
  // own key — so its own clock — rather than what was left of the first one's.
  const [shown, setShown] = React.useState({alert, count: 0});
  if (shown.alert !== alert) {
    setShown({alert, count: shown.count + 1});
  }

  const key = alert ? `${shown.count}:${alert.type}:${alert.message}` : null;
  const focused = key !== null && focusedKey === key;
  const severity: BannerSeverity =
    (alert ? SEVERITY[alert.type] : undefined) ?? 'info';

  return alert ? (
    <Snackbar
      // A new alert starts its own clock.
      key={key}
      open
      // null = no timer while it is being read with the keyboard. MUI stops
      // it itself under the pointer.
      autoHideDuration={focused ? null : HIDE_MS}
      resumeHideDuration={HIDE_MS}
      onClose={handleSnackbarClose}
      anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
      sx={{
        bottom: {
          xs: 'var(--snackbar-bottom, 8px)',
          sm: 'var(--snackbar-bottom, 24px)',
        },
      }}>
      <Box
        data-testid="alert-snackbar"
        onFocus={() => setFocusedKey(key)}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === 'Escape' && !event.defaultPrevented) {
            event.preventDefault();
            handleClose();
          }
        }}
        onBlur={(event: React.FocusEvent<HTMLElement>) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node)) {
            setFocusedKey(null);
          }
        }}
        sx={(theme) => ({
          width: {xs: '100%', sm: 'auto'},
          maxWidth: {sm: 480},
          borderRadius: `${radius.sm}px`,
          // A floating surface (spec: elevation "floating").
          boxShadow: elevation.floating[theme.palette.mode],
        })}>
        <Banner
          severity={severity}
          onClose={handleClose}
          closeLabel={formatMessage(messages.common.close)}>
          {alert.message}
        </Banner>
      </Box>
    </Snackbar>
  ) : null;
};

export default AlertSnackbar;
